import * as Sentry from "@sentry/react";
import { createBrowserRouter, Navigate, Outlet, RouteObject, useLocation } from "react-router";
import { RouterProvider } from "react-router/dom";

import { Root } from "./Root";
import { EmbeddingLayout } from "./Layout/EmbeddingLayout";
import { ErrorBoundary } from "./ErrorBoundary";
import { Protected } from "./Protected";
import { Unprotected } from "./Unprotected";
import { LogoutDialog } from "./LogoutDialog";
import { BusinessContextGuard } from "./guards/BusinessContextGuard";
import { OnboardingProgressProvider } from "./onboardingStatus";

import { SFullAppPage } from "./pages/SEmbed";
import { AIDashboards } from "./pages/AIDashboards";
import { AISchedules } from "./pages/AISchedules";
import { CHAT_PATH } from "./constants";

import { Login } from "./pages/Login";
import { ResetPassword } from "./pages/ResetPassword";
import { Settings } from "./pages/Settings";
import { SettingsUsers } from "./pages/Settings/Users";
import { SettingsBusinesses } from "./pages/Settings/Businesses";
import { SettingsBudget } from "./pages/Settings/Budget";

// Onboarding Components

function LegacyChatRedirect() {
  const location = useLocation();
  const legacyPath = "/labeeb-chat";
  const suffix = location.pathname.slice(legacyPath.length);

  return <Navigate to={`${CHAT_PATH}${suffix}${location.search}${location.hash}`} replace />;
}

const routes: RouteObject[] = [
  {
    path: "/",
    errorElement: <ErrorBoundary />,
    HydrateFallback: ErrorBoundary,
    element: <Root />,
    children: [
      {
        path: "login",
        element: (
          <Unprotected>
            <Login />
          </Unprotected>
        ),
      },
      // Password-reset links sent to older mobile builds need a browser fallback.
      {
        path: "reset-password",
        element: <ResetPassword />,
      },
      {
        path: "install",
        element: <Navigate to="/login" replace />,
      },
      {
        path: "already-installed",
        element: <Navigate to="/login" replace />,
      },
      {
        path: "multi-business/success",
        element: <Navigate to="/login" replace />,
      },
      {
        path: "multi-business/already-installed",
        element: <Navigate to="/login" replace />,
      },
      {
        path: "initial-onboarding",
        element: <Navigate to="/login" replace />,
      },
      {
        path: "onboarding/error",
        element: <Navigate to="/login" replace />,
      },
      {
        path: "onboarding/:businessId",
        element: <Navigate to="/login" replace />,
      },
      {
        path: "/",
        element: (
          <Protected>
            <OnboardingProgressProvider>
              <BusinessContextGuard>
                <Outlet />
              </BusinessContextGuard>
            </OnboardingProgressProvider>
            <LogoutDialog />
          </Protected>
        ),
        children: [
          {
            path: "settings",
            element: <Settings />,
            children: [
              {
                path: "users",
                element: <SettingsUsers />,
              },
              {
                path: "businesses",
                element: <SettingsBusinesses />,
              },
              {
                path: "budget",
                element: <SettingsBudget />,
              },
            ],
          },
          {
            element: <EmbeddingLayout />,
            children: [
              {
                index: true,
                element: <Navigate to={CHAT_PATH} replace />,
              },
              {
                path: `${CHAT_PATH.slice(1)}/*`,
                element: <SFullAppPage />,
              },
              {
                path: "labeeb-chat/*",
                element: <LegacyChatRedirect />,
              },
              {
                path: "ai-dashboards",
                element: <AIDashboards />,
              },
              {
                path: "ai-schedules",
                element: <AISchedules />,
              },
            ],
          },
          // Unknown paths, including retired analytics pages, open the home page.
          {
            path: "*",
            element: <Navigate to={CHAT_PATH} replace />,
          },
        ],
      },
    ],
  },
];

// Export routes for testing purposes
/* eslint-disable react-refresh/only-export-components */
export { routes };

const router = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)(routes);

export function Router() {
  return <RouterProvider router={router} />;
}
